import {Injectable} from '@angular/core';
import {LoadingController} from "@ionic/angular";
import {HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";
import {CreatePartyViewModel} from "../viewmodels/request/CreatePartyViewModel";
import {BehaviorSubject, catchError, firstValueFrom, mergeMap, Observable, of} from "rxjs";
import {environment} from "../../../environments/environment";
import {LoginViewModel} from "../viewmodels/request/LoginViewModel";
import {LoginSessionResponseViewModel} from "../viewmodels/response/LoginSessionResponseViewModel";
import {Preferences} from "@capacitor/preferences";
import {BasicResponseViewModel} from "../viewmodels/response/BasicResponseViewModel";
import {SILENT_REQUEST} from "../../http-context-tokens";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  static readonly ACCESS_EXPIRY_KEY = '_tokenTimestamp';
  static readonly REFRESH_EXPIRY_KEY = '_refreshTimestamp';
  static readonly REFRESH_EXPIRED = 'REFRESH_EXPIRED';
  static readonly DEFAULT_ACCESS_TTL_MS = 30 * 60 * 1000;

  private readonly REFRESH_BUFFER_MS = 2 * 60 * 1000;
  /** Never fire a refresh timer sooner than this interval (prevents battery drain). */
  private readonly MIN_REFRESH_INTERVAL_MS = 30 * 1000;
  /** After this many consecutive silent-refresh failures, stop retrying and sign the user out.
   *  A genuinely invalid session already triggers an immediate logout via the 401/403 handler
   *  below — this only covers repeated *transient* failures (timeouts, 5xx, offline blips) that
   *  never resolve. Without this cap, the countdown/banner is left showing "Session expired"
   *  forever with no further attempt to recover and no path back to a clear signed-out state. */
  private readonly MAX_SILENT_REFRESH_RETRIES = 5;
  /** Backoff between silent-refresh retries — capped so a persistently unreachable backend
   *  doesn't hammer the refresh endpoint, while still giving transient issues time to clear. */
  private readonly RETRY_BACKOFF_MS = [15_000, 30_000, 60_000, 120_000, 300_000];

  private readonly refreshTokenUrl = 'party/auth/refreshToken';
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  /** Consecutive silent-refresh failures since the timer was last (re)armed via a successful
   *  schedule (fresh login, app boot, or a successful refresh) — reset in scheduleTokenRefresh(). */
  private silentRefreshFailureCount = 0;
  private loggingOut = false;
  private pendingValidation: Promise<boolean> | null = null;

  /** Milliseconds remaining until tokenExpiry, ticking every second; null when there's no active session. */
  private readonly sessionRemainingMsSubject = new BehaviorSubject<number | null>(null);
  readonly sessionRemainingMs$: Observable<number | null> = this.sessionRemainingMsSubject.asObservable();
  private sessionCountdownIntervalId: ReturnType<typeof setInterval> | null = null;

  private loadingController: LoadingController;
  private httpClient: HttpClient;

  constructor(loadingController: LoadingController, httpClient: HttpClient) {
    this.loadingController = loadingController;
    this.httpClient = httpClient;
  }

  createPartyRequest(createPartyRequest: CreatePartyViewModel): Observable<HttpResponse<any>> {
    const endpoint = environment.reactiveServer.concat('party/auth/register')
    const httpHeaders = new HttpHeaders({'Content-Type':'application/json'});
    return this.httpClient.post<HttpResponse<any>>(endpoint, JSON.stringify(createPartyRequest), {headers: httpHeaders});
  }

  loginPartyRequest(loginRequest: LoginViewModel, ticketId?: string): Observable<LoginSessionResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/login');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    const params = ticketId ? new HttpParams().set('ticketId', ticketId) : undefined;
    return this.httpClient.post<LoginSessionResponseViewModel>(endpoint, JSON.stringify(loginRequest), {headers: httpHeaders, withCredentials: true, params});
  }

  /**
   * Real WebAuthn/FIDO2 passkey ceremony endpoints — distinct from biometricsEnrollment/
   * biometricsAuthentication above, which are the legacy device-token (Base64'd PIN) flow.
   * register/* requires an authenticated session; login/* is pre-authentication (identifies
   * the party by phone number, same as loginPartyRequest's first step).
   */
  webauthnRegisterBegin(): Observable<PublicKeyCredentialCreationOptionsJSON> {
    const endpoint = environment.reactiveServer.concat('party/auth/webauthn/register/begin');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient.post<PublicKeyCredentialCreationOptionsJSON>(endpoint, null, {headers: httpHeaders, withCredentials: true});
  }

  webauthnRegisterFinish(body: RegistrationResponseJSON & { deviceName?: string }): Observable<BasicResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/webauthn/register/finish');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    const requestBody = {
      credentialId: body.id,
      clientDataJSON: body.response.clientDataJSON,
      attestationObject: body.response.attestationObject,
      transports: body.response.transports,
      deviceName: body.deviceName,
    };
    return this.httpClient.post<BasicResponseViewModel>(endpoint, JSON.stringify(requestBody), {headers: httpHeaders, withCredentials: true});
  }

  webauthnLoginBegin(dialingCode: string, username: string): Observable<PublicKeyCredentialRequestOptionsJSON> {
    const endpoint = environment.reactiveServer.concat('party/auth/webauthn/login/begin');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient.post<PublicKeyCredentialRequestOptionsJSON>(endpoint, JSON.stringify({dialingCode, username}), {headers: httpHeaders, withCredentials: true});
  }

  webauthnLoginFinish(dialingCode: string, username: string, assertion: AuthenticationResponseJSON): Observable<LoginSessionResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/webauthn/login/finish');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    const requestBody = {
      dialingCode,
      username,
      credentialId: assertion.id,
      clientDataJSON: assertion.response.clientDataJSON,
      authenticatorData: assertion.response.authenticatorData,
      signature: assertion.response.signature,
    };
    return this.httpClient.post<LoginSessionResponseViewModel>(endpoint, JSON.stringify(requestBody), {headers: httpHeaders, withCredentials: true});
  }

  /**
   * Hardened native quick sign-in: an ECDSA P-256 keypair generated on-device (WebCrypto,
   * non-extractable private key) gated behind NativeBiometric's Face ID/Touch ID prompt —
   * replaces the legacy biometricsEnrollment/biometricsAuthentication Base64(PIN) flow above.
   * See DeviceKeyService.
   */
  deviceKeyEnroll(deviceId: string, publicKeySpki: string): Observable<BasicResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/deviceKey/enroll');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient.post<BasicResponseViewModel>(endpoint, JSON.stringify({deviceId, publicKeySpki}), {headers: httpHeaders, withCredentials: true});
  }

  deviceKeyLoginBegin(deviceId: string, ticketId?: string): Observable<{ challenge: string }> {
    const endpoint = environment.reactiveServer.concat('party/auth/deviceKey/login/begin');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    const params = ticketId ? new HttpParams().set('ticketId', ticketId) : undefined;
    return this.httpClient.post<{ challenge: string }>(endpoint, JSON.stringify({deviceId}), {headers: httpHeaders, withCredentials: true, params});
  }

  deviceKeyLoginFinish(deviceId: string, signature: string, ticketId?: string): Observable<LoginSessionResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/deviceKey/login/finish');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    const params = ticketId ? new HttpParams().set('ticketId', ticketId) : undefined;
    return this.httpClient.post<LoginSessionResponseViewModel>(endpoint, JSON.stringify({deviceId, signature}), {headers: httpHeaders, withCredentials: true, params});
  }

  acceptTerms(version: string): Observable<BasicResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/terms');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient.put<BasicResponseViewModel>(endpoint, JSON.stringify({accepted: true, version}), {headers: httpHeaders, withCredentials: true});
  }

  resetPassCodeRequest(loginRequest: LoginViewModel): Observable<LoginSessionResponseViewModel> {
    const endpoint = environment.reactiveServer.concat('party/auth/credentialManagement')
    const httpHeaders = new HttpHeaders({'Content-Type':'application/json'});
    return this.httpClient.put<LoginSessionResponseViewModel>(endpoint, JSON.stringify(loginRequest), {headers: httpHeaders, withCredentials: true});
  }

  syncTokenStatus(): Observable<boolean> {
    const endpoint = environment.reactiveServer.concat('party/auth/sync');
    const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.get<BasicResponseViewModel>(endpoint, {headers: httpHeaders, withCredentials: true}).pipe(
      mergeMap(async response => {
        await this.initTokenRefresh();
        return response.success;
      }),
      catchError(error => {
        console.error();
        return of(true);
      })
    )
  }

  async signOut() {
    const endpoint = environment.reactiveServer.concat('party/auth/clearSession');
    const httpHeaders = new HttpHeaders({'Content-Type':'application/json'});
    return this.httpClient.patch<BasicResponseViewModel>(endpoint, null, {headers: httpHeaders, withCredentials: true})
      .subscribe({
        next: async () => await this.clearSession(),
        error: async () => await this.clearSession()
      });
  }

  async clearSession() {
    this.stopRefreshTimer();
    this.stopCountdownTicker();
    await Preferences.clear();
    this.redirectTo('/');
  }

  async establishSession(response: LoginSessionResponseViewModel): Promise<void> {
    const accessExpiry = AuthService.toEpochMs(response?.tokenExpiry) ?? Date.now() + AuthService.DEFAULT_ACCESS_TTL_MS;
    await Preferences.set({ key: AuthService.ACCESS_EXPIRY_KEY, value: String(accessExpiry) });
    const refreshExpiry = AuthService.toEpochMs(response?.refreshTokenExpiry);
    if (refreshExpiry) {
      await Preferences.set({ key: AuthService.REFRESH_EXPIRY_KEY, value: String(refreshExpiry) });
    } else {
      await Preferences.remove({ key: AuthService.REFRESH_EXPIRY_KEY });
    }
    this.loggingOut = false;
    await this.initTokenRefresh();
  }

  async hasStoredSession(): Promise<boolean> {
    return (await this.readExpiry(AuthService.ACCESS_EXPIRY_KEY)) !== null;
  }

  async isRefreshPossible(): Promise<boolean> {
    const refreshExpiry = await this.readExpiry(AuthService.REFRESH_EXPIRY_KEY);
    return refreshExpiry === null || refreshExpiry > Date.now();
  }

  async ensureValidSession(): Promise<boolean> {
    const accessExpiry = await this.readExpiry(AuthService.ACCESS_EXPIRY_KEY);
    if (accessExpiry === null) {
      return false;
    }
    if (accessExpiry > Date.now()) {
      return true;
    }
    if (!(await this.isRefreshPossible())) {
      await this.logout401();
      return false;
    }
    if (!this.pendingValidation) {
      this.pendingValidation = firstValueFrom(this.performRefresh(true)).then(async (outcome) => {
        if (outcome !== 'refreshed') {
          await this.logout401();
        }
        return outcome === 'refreshed';
      }).finally(() => this.pendingValidation = null);
    }
    return this.pendingValidation;
  }

  async initTokenRefresh(): Promise<void> {
    const accessExpiry = await this.readExpiry(AuthService.ACCESS_EXPIRY_KEY);
    if (accessExpiry === null) {
      return;
    }
    if (accessExpiry <= Date.now()) {
      await this.ensureValidSession();
      return;
    }
    if (!(await this.isRefreshPossible())) {
      await this.logout401();
      return;
    }
    this.scheduleTokenRefresh(accessExpiry);
  }

  private scheduleTokenRefresh(sessionExpirationTimestamp: number): void {
    this.stopRefreshTimer();
    this.silentRefreshFailureCount = 0;

    const delay = Math.max(
      sessionExpirationTimestamp - Date.now() - this.REFRESH_BUFFER_MS,
      this.MIN_REFRESH_INTERVAL_MS
    );

    this.refreshTimerId = setTimeout(() => this.silentRefresh(), delay);
    this.startCountdownTicker(sessionExpirationTimestamp);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private startCountdownTicker(sessionExpirationTimestamp: number): void {
    if (this.sessionCountdownIntervalId !== null) {
      clearInterval(this.sessionCountdownIntervalId);
    }
    const tick = () => {
      const remaining = sessionExpirationTimestamp - Date.now();
      if (remaining <= 0) {
        this.stopCountdownTicker();
        void this.ensureValidSession();
        return;
      }
      this.sessionRemainingMsSubject.next(remaining);
    };
    tick();
    if (sessionExpirationTimestamp > Date.now()) {
      this.sessionCountdownIntervalId = setInterval(tick, 1000);
    }
  }

  private stopCountdownTicker(): void {
    if (this.sessionCountdownIntervalId !== null) {
      clearInterval(this.sessionCountdownIntervalId);
      this.sessionCountdownIntervalId = null;
    }
    this.sessionRemainingMsSubject.next(null);
  }

  private performRefresh(silent: boolean): Observable<RefreshOutcome> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' });
    return this.httpClient
      .put<LoginSessionResponseViewModel>(
        environment.reactiveServer.concat(this.refreshTokenUrl),
        null,
        { headers: httpHeaders, withCredentials: true, context: new HttpContext().set(SILENT_REQUEST, silent) }
      )
      .pipe(
        mergeMap(async (response): Promise<RefreshOutcome> => {
          const accessExpiry = AuthService.toEpochMs(response?.tokenExpiry);
          if (!accessExpiry) {
            return 'transient';
          }
          await Preferences.set({ key: AuthService.ACCESS_EXPIRY_KEY, value: String(accessExpiry) });
          const refreshExpiry = AuthService.toEpochMs(response?.refreshTokenExpiry);
          if (refreshExpiry) {
            await Preferences.set({ key: AuthService.REFRESH_EXPIRY_KEY, value: String(refreshExpiry) });
          }
          this.scheduleTokenRefresh(accessExpiry);
          return 'refreshed';
        }),
        catchError((error) => of<RefreshOutcome>(AuthService.isFatalRefreshError(error) ? 'expired' : 'transient'))
      );
  }

  private silentRefresh(): void {
    this.performRefresh(true).subscribe({
      next: (outcome) => {
        if (outcome === 'refreshed') {
          return;
        }
        if (outcome === 'expired') {
          void this.logout401();
          return;
        }

        this.silentRefreshFailureCount++;
        if (this.silentRefreshFailureCount > this.MAX_SILENT_REFRESH_RETRIES) {
          this.refreshTimerId = null;
          void this.logout401();
          return;
        }

        const backoff = this.RETRY_BACKOFF_MS[
          Math.min(this.silentRefreshFailureCount - 1, this.RETRY_BACKOFF_MS.length - 1)
        ];
        this.refreshTimerId = setTimeout(() => this.silentRefresh(), backoff);
      }
    });
  }

  async manualRefresh(): Promise<boolean> {
    const outcome = await firstValueFrom(this.performRefresh(false));
    if (outcome === 'expired') {
      await this.logout401();
    }
    return outcome === 'refreshed';
  }

  async logout401(): Promise<void> {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.stopRefreshTimer();
    this.stopCountdownTicker();
    await Preferences.clear();
    this.redirectTo('/signIn');
  }

  redirectTo(path: string): void {
    window.location.href = path;
  }

  private async readExpiry(key: string): Promise<number | null> {
    const stored = await Preferences.get({ key });
    return AuthService.toEpochMs(stored.value);
  }

  static toEpochMs(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed < 100_000_000_000 ? parsed * 1000 : parsed;
  }

  static isFatalRefreshError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (error.status === 401 || error.status === 403) {
      return true;
    }
    return String(error.error?.errorMessage ?? error.error?.errorCode ?? '').includes(AuthService.REFRESH_EXPIRED);
  }
}

export type RefreshOutcome = 'refreshed' | 'expired' | 'transient';
